import React, { useState, useEffect, useRef } from "react";
import api from "@/api";

/**
 * Reusable ticker/company autocomplete.
 *
 * Controlled input (value/onChange) with a debounced dropdown of /securities/search
 * results, keyboard navigation (↑/↓/Enter/Esc) and click-outside dismissal. Calls
 * onSelect(result) when a result is chosen.
 *
 * Used by the header search (variant="header") and CreateAlertDialog (variant="field").
 *
 * Props:
 *  - value, onChange(text): controlled input text
 *  - onSelect(result): { ticker, name, current_price, exchange, asset_class }
 *  - disabled, placeholder, autoFocus
 *  - variant: "field" (default) | "header"
 *  - minChars (default 1), limit (default 10)
 */
const TickerSearch = ({
  value,
  onChange,
  onSelect,
  disabled = false,
  placeholder = "Search ticker or company…",
  autoFocus = false,
  variant = "field",
  minChars = 1,
  limit = 10,
}) => {
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const justSelectedRef = useRef(false);

  // Debounced search
  useEffect(() => {
    if (disabled || !value || value.length < minChars) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    // Skip the search that fires immediately after a selection.
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await api.get(`/securities/search`, {
          params: { q: value, limit },
        });
        setResults(response.data);
        setShowDropdown(response.data.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Ticker search error:", error);
        setResults([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [value, disabled, minChars, limit]);

  const handleSelect = (result) => {
    justSelectedRef.current = true;
    setShowDropdown(false);
    setResults([]);
    setSelectedIndex(-1);
    if (onSelect) onSelect(result);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === "Escape") {
      // Close the dropdown without bubbling to any modal-level Escape handler.
      e.stopPropagation();
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isHeader = variant === "header";

  const inputClassName = isHeader
    ? "w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
    : "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400";

  return (
    <div className="relative">
      {isHeader && (
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 110-14 7 7 0 010 14z" />
        </svg>
      )}

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange && onChange(e.target.value.toUpperCase())}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        className={inputClassName}
        disabled={disabled}
        autoComplete="off"
        autoFocus={autoFocus}
      />

      {/* Loading indicator */}
      {loading && (
        <div className={isHeader ? "absolute right-3 top-1/2 -translate-y-1/2" : "absolute right-3 top-9"}>
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      )}

      {/* Autocomplete dropdown */}
      {showDropdown && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {results.map((result, index) => (
            <button
              key={result.ticker}
              type="button"
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${
                index === selectedIndex ? "bg-blue-50 dark:bg-blue-900/20" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-white">{result.ticker}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 truncate">{result.name}</div>
                </div>
                {result.current_price != null && (
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-2 shrink-0">
                    ${result.current_price.toFixed(2)}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TickerSearch;
